// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Integrations API Client
 *
 * Calls communications-service via Advisor BFF (/api/integrations/**).
 * Today's surface: Google Calendar Read (today's meetings widget +
 * per-client meetings tab + manual link/unlink + re-auth banner state).
 * Microsoft + email follow in later phases.
 */

import api from './advisorApi';

export type IntegrationProvider = 'GOOGLE' | 'MICROSOFT';
export type ConnectionStatus = 'ACTIVE' | 'NEEDS_REAUTH' | 'DISCONNECTED';
export type LinkMethod = 'AUTO_EMAIL_MATCH' | 'MANUAL' | 'PLATFORM_CREATED';

export interface LinkedClientDTO {
  clientProfileId: string;
  linkMethod: LinkMethod;
}

export interface CalendarEventDTO {
  eventId: string;
  externalId: string;
  subject: string | null;
  bodyPreview: string | null;
  startsAt: string | null;
  endsAt: string | null;
  location: string | null;
  cancelled: boolean;
  onlineMeeting: boolean | null;
  onlineMeetingUrl: string | null;
  linkedClients: LinkedClientDTO[];
}

export interface CalendarConnectionStatusDTO {
  hasConnection: boolean;
  provider: IntegrationProvider | null;
  status: ConnectionStatus | null;
  grantedScopes: string | null;
  needsReauth: boolean;
}

export const integrationsApi = {
  /**
   * Today's Meetings widget. Default window: today 00:00 UTC → +14 days.
   * Pass a provider to narrow to that connection; otherwise the first
   * ACTIVE connection wins.
   */
  async getTodaysMeetings(params?: {
    provider?: IntegrationProvider;
    from?: string;
    to?: string;
  }): Promise<CalendarEventDTO[]> {
    const response = await api.get<CalendarEventDTO[]>('/integrations/calendar/today', {
      params,
    });
    return response.data;
  },

  /**
   * Client profile Meetings tab. Default window: 90d back → 90d forward.
   */
  async getMeetingsForClient(clientId: string, params?: {
    from?: string;
    to?: string;
  }): Promise<CalendarEventDTO[]> {
    const response = await api.get<CalendarEventDTO[]>(
      `/integrations/calendar/by-client/${clientId}`,
      { params },
    );
    return response.data;
  },

  /**
   * Connection status for the calling advisor. Drives the empty-state +
   * re-auth banner branches.
   */
  async getConnectionStatus(provider?: IntegrationProvider): Promise<CalendarConnectionStatusDTO> {
    const response = await api.get<CalendarConnectionStatusDTO>(
      '/integrations/calendar/connection-status',
      { params: provider ? { provider } : {} },
    );
    return response.data;
  },

  /**
   * Manually link a cached event to a client profile. Idempotent.
   */
  async linkClient(eventId: string, clientProfileId: string): Promise<void> {
    await api.post(`/integrations/calendar/events/${eventId}/links`, { clientProfileId });
  },

  /**
   * Remove a manual link. The backend refuses (HTTP 409) for non-MANUAL
   * links — those are the linker's responsibility.
   */
  async unlinkClient(eventId: string, clientProfileId: string): Promise<void> {
    await api.delete(`/integrations/calendar/events/${eventId}/links/${clientProfileId}`);
  },
};
